import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, LOGO_URL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { LogOut, Search, Trash2, MessageCircle, Phone, Mail, RefreshCw } from "lucide-react";
import { PRODUCTS } from "../lib/products";
import { buildWhatsAppUrl } from "../lib/whatsapp";

const STATUS_COLORS = {
  new: "bg-[#006A4E]/10 text-[#006A4E] border-[#006A4E]/30",
  contacted: "bg-amber-100 text-amber-800 border-amber-300",
  closed: "bg-slate-200 text-slate-700 border-slate-300",
};

export default function AdminDashboardPage() {
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_product: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProduct, setFilterProduct] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("mybeema_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, []); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const [eRes, sRes] = await Promise.all([api.get("/enquiries"), api.get("/enquiries/stats")]);
      setEnquiries(eRes.data);
      setStats(sRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("mybeema_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load enquiries");
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("mybeema_token");
    localStorage.removeItem("mybeema_user");
    navigate("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      setEnquiries((arr) => arr.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await api.delete(`/enquiries/${id}`);
      setEnquiries((arr) => arr.filter((e) => e.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const filtered = useMemo(() => {
    return enquiries.filter((e) => {
      if (filterStatus !== "all" && e.status !== filterStatus) return false;
      if (filterProduct !== "all" && e.product_id !== filterProduct) return false;
      if (q) {
        const s = q.toLowerCase();
        return (
          e.name.toLowerCase().includes(s) ||
          e.phone.includes(s) ||
          (e.email || "").toLowerCase().includes(s) ||
          (e.city || "").toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [enquiries, q, filterStatus, filterProduct]);

  const statusCards = [
    { key: "total", label: "Total Enquiries", value: stats.total },
    { key: "new", label: "New", value: stats.by_status?.new || 0 },
    { key: "contacted", label: "Contacted", value: stats.by_status?.contacted || 0 },
    { key: "closed", label: "Closed", value: stats.by_status?.closed || 0 },
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="logo" className="h-11 w-11 rounded-full object-cover ring-2 ring-[#006A4E]/20" />
            <div>
              <div className="font-display text-xl font-bold text-[#0B192C]">MY BEEMA<span className="text-[#006A4E]">.in</span></div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#4A5568]">Admin Console</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button data-testid="admin-refresh-btn" variant="outline" size="sm" onClick={load} className="rounded-full border-[#E2E8F0]">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button data-testid="admin-logout-btn" variant="ghost" onClick={logout} className="rounded-full text-[#0B192C] hover:text-[#006A4E]">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-2">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#006A4E]">Dashboard</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0B192C] mt-2">Enquiries Overview</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {statusCards.map((c) => (
            <div key={c.key} className="bg-white rounded-2xl border border-[#E2E8F0] p-5" data-testid={`stat-card-${c.key}`}>
              <div className="text-xs uppercase tracking-[0.2em] text-[#4A5568]">{c.label}</div>
              <div className="font-display text-4xl font-bold mt-2 text-[#0B192C]">{c.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-10 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5568]" />
            <Input
              data-testid="enquiry-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, phone, email or city"
              className="pl-10 rounded-full bg-white"
            />
          </div>
          <Select value={filterProduct} onValueChange={setFilterProduct}>
            <SelectTrigger data-testid="filter-product" className="md:w-56 rounded-full bg-white"><SelectValue placeholder="Product" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All products</SelectItem>
              {PRODUCTS.map((p) => (<SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status" className="md:w-40 rounded-full bg-white"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="mt-6 bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-[#4A5568]">
                  <th className="py-4 px-5">Customer</th>
                  <th className="py-4 px-5">Product</th>
                  <th className="py-4 px-5">Details</th>
                  <th className="py-4 px-5">Received</th>
                  <th className="py-4 px-5">Status</th>
                  <th className="py-4 px-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-16 text-[#4A5568]">Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-16 text-[#4A5568]" data-testid="no-enquiries">No enquiries yet.</td></tr>
                ) : (
                  filtered.map((e) => (
                    <tr key={e.id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-[#F8F9FA]/60" data-testid={`enquiry-row-${e.id}`}>
                      <td className="py-4 px-5">
                        <div className="font-semibold text-[#0B192C]">{e.name}</div>
                        <div className="text-xs text-[#4A5568] flex items-center gap-3 mt-1">
                          <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 hover:text-[#006A4E]"><Phone className="w-3 h-3" />{e.phone}</a>
                          {e.email && <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 hover:text-[#006A4E]"><Mail className="w-3 h-3" />{e.email}</a>}
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <div className="font-medium text-[#0B192C]">{e.product_title}</div>
                        {e.city && <div className="text-xs text-[#4A5568] mt-0.5">{e.city}</div>}
                      </td>
                      <td className="py-4 px-5 max-w-xs">
                        {e.extra && <div className="text-xs text-[#4A5568]"><span className="font-semibold text-[#0B192C]">Extra:</span> {e.extra}</div>}
                        {e.notes && <div className="text-xs text-[#4A5568] mt-1 line-clamp-2"><span className="font-semibold text-[#0B192C]">Notes:</span> {e.notes}</div>}
                        {!e.extra && !e.notes && <span className="text-xs text-[#4A5568]">—</span>}
                      </td>
                      <td className="py-4 px-5 text-xs text-[#4A5568] whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </td>
                      <td className="py-4 px-5">
                        <Select value={e.status} onValueChange={(v) => updateStatus(e.id, v)}>
                          <SelectTrigger data-testid={`status-select-${e.id}`} className="w-32 h-8 text-xs rounded-full">
                            <Badge variant="outline" className={`border ${STATUS_COLORS[e.status] || ""} capitalize`}>{e.status}</Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-4 px-5 text-right">
                        <div className="inline-flex items-center gap-1">
                          <a
                            data-testid={`whatsapp-action-${e.id}`}
                            href={buildWhatsAppUrl(`91${e.phone}`, `Hi ${e.name}, regarding your ${e.product_title} enquiry on MY BEEMA…`)}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[#25D366] hover:bg-[#25D366]/10"
                            title="Message on WhatsApp"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </a>
                          <button
                            data-testid={`delete-action-${e.id}`}
                            onClick={() => remove(e.id)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-red-500 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
